import { useMemo, useState } from "react"
import { useKey } from "react-use"
import {
  createContext as createSelectorContext,
  useContextSelector
} from "use-context-selector"
import { useEditorStateSelector } from "./EditorStateProvider"

type EditorUIState = {
  focusedBlockIndex: number | null
  setFocusedBlockIndex: (index: number | null) => void
  triggeredAddBlock: number | null
  setTriggeredAddBlock: (index: number | null) => void
}

const EditorUIStateContext = createSelectorContext<EditorUIState | null>(null)

export const useEditorUI = <T = EditorUIState,>(
  selector?: (state: EditorUIState) => T
): T =>
  useContextSelector(EditorUIStateContext, (state) => {
    if (!state) return {} as T
    if (selector) return selector(state)

    return state as T
  })

export const EditorUIProvider = ({ children }) => {
  const handleToggleMode = useEditorStateSelector(
    (state) => state.handleToggleMode
  )

  const [focusedBlockIndex, setFocusedBlockIndex] = useState<
    EditorUIState["focusedBlockIndex"] | null
  >(null)

  const [triggeredAddBlock, setTriggeredAddBlock] = useState<
    EditorUIState["triggeredAddBlock"] | null
  >(null)

  useKey(
    "Enter",
    (event) => {
      // Cmd/ctrl + option/alt + Enter to open the next add block menu
      const toggleAddBlockCombo =
        event.altKey && event.ctrlKey && !event.shiftKey
      if (toggleAddBlockCombo) {
        setTriggeredAddBlock(
          focusedBlockIndex !== null ? focusedBlockIndex + 1 : -1
        )
      }

      // Cmd/ctrl + option/alt + shift + Enter to open the next add block menu
      const modeShortcut = event.altKey && event.shiftKey && event.ctrlKey
      if (modeShortcut && handleToggleMode) {
        handleToggleMode()
      }
    },
    {}, // options
    [focusedBlockIndex] // deps
  )

  const value = useMemo(
    () => ({
      focusedBlockIndex,
      setFocusedBlockIndex,
      triggeredAddBlock,
      setTriggeredAddBlock
    }),
    [focusedBlockIndex, triggeredAddBlock]
  )

  return (
    <EditorUIStateContext.Provider value={value}>
      {children}
    </EditorUIStateContext.Provider>
  )
}

export default EditorUIProvider
